import { useEffect, useState } from 'react';
import { Package, Car } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_STATUSES = [
    { value: 'en_cours', label: 'En cours', color: 'bg-yellow-500/20 text-yellow-400' },
    { value: 'geree', label: 'Gérée', color: 'bg-blue-500/20 text-blue-400' },
    { value: 'payee', label: 'Payée', color: 'bg-green-500/20 text-green-400' },
    { value: 'livree', label: 'Livrée', color: 'bg-emerald-500/20 text-emerald-400' },
    { value: 'annulee', label: 'Annulée', color: 'bg-red-500/20 text-red-400' },
    { value: 'remboursee', label: 'Remboursée', color: 'bg-purple-500/20 text-purple-400' },
];

const CAR_STATUSES = [
    { value: 'en_cours', label: 'En cours', color: 'bg-yellow-500/20 text-yellow-400' },
    { value: 'avance_versee', label: 'Avance versée', color: 'bg-blue-500/20 text-blue-400' },
    { value: 'payee', label: 'Payée', color: 'bg-green-500/20 text-green-400' },
    { value: 'livree', label: 'Livrée', color: 'bg-emerald-500/20 text-emerald-400' },
    { value: 'annulee', label: 'Annulée', color: 'bg-red-500/20 text-red-400' },
    { value: 'remboursee', label: 'Remboursée', color: 'bg-purple-500/20 text-purple-400' },
];

export default function AdminOrdersPage() {
    const [productOrders, setProductOrders] = useState([]);
    const [carOrders, setCarOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const [prodRes, carRes] = await Promise.all([
                axios.get(`${API}/orders/products`),
                axios.get(`${API}/orders/cars`)
            ]);
            setProductOrders(prodRes.data);
            setCarOrders(carRes.data);
        } catch (error) {
            console.error('Error fetching orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateProductStatus = async (id, statut) => {
        try {
            await axios.put(`${API}/orders/products/${id}/status`, { statut });
            toast.success('Statut mis à jour');
            fetchOrders();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const updateCarStatus = async (id, statut) => {
        try {
            await axios.put(`${API}/orders/cars/${id}/status`, { statut });
            toast.success('Statut mis à jour');
            fetchOrders();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const getStatusBadge = (statut, statuses) => {
        const status = statuses.find(s => s.value === statut);
        return status ? <span className={`px-2 py-1 rounded text-xs ${status.color}`}>{status.label}</span> : statut;
    };

    const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

    return (
        <div data-testid="admin-orders-page">
            <div className="mb-6">
                <h1 className="font-heading text-2xl font-medium text-white">Commandes</h1>
                <p className="text-mmd-silver/70 text-sm">Gérez les commandes de produits et de véhicules</p>
            </div>

            <Tabs defaultValue="products" className="w-full">
                <TabsList className="mb-6">
                    <TabsTrigger value="products" className="gap-2" data-testid="orders-tab-products">
                        <Package size={16} />
                        Produits ({productOrders.length})
                    </TabsTrigger>
                    <TabsTrigger value="cars" className="gap-2" data-testid="orders-tab-cars">
                        <Car size={16} />
                        Voitures ({carOrders.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="products">
                    {loading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map(i => <div key={i} className="bg-mmd-surface-dark rounded-lg p-4 animate-pulse h-24" />)}
                        </div>
                    ) : productOrders.length === 0 ? (
                        <div className="text-center py-12 text-mmd-silver/50">
                            <p>Aucune commande de produit</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {productOrders.map(order => (
                                <div key={order.id} className="bg-mmd-surface-dark border border-white/10 rounded-lg p-4" data-testid={`product-order-${order.id}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div>
                                            <h3 className="font-medium text-white">{order.product_nom}</h3>
                                            <p className="text-sm text-mmd-silver/60">
                                                {order.client.prenom} {order.client.nom} - {order.client.telephone}
                                            </p>
                                            <p className="text-xs text-mmd-silver/40 mt-1">{formatDate(order.date)}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {getStatusBadge(order.statut, PRODUCT_STATUSES)}
                                            <Select value={order.statut} onValueChange={(v) => updateProductStatus(order.id, v)}>
                                                <SelectTrigger className="w-36" data-testid={`product-order-status-${order.id}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {PRODUCT_STATUSES.map(s => (
                                                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="cars">
                    {loading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map(i => <div key={i} className="bg-mmd-surface-dark rounded-lg p-4 animate-pulse h-24" />)}
                        </div>
                    ) : carOrders.length === 0 ? (
                        <div className="text-center py-12 text-mmd-silver/50">
                            <p>Aucune commande de voiture</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {carOrders.map(order => (
                                <div key={order.id} className="bg-mmd-surface-dark border border-white/10 rounded-lg p-4" data-testid={`car-order-${order.id}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div>
                                            <h3 className="font-medium text-white">{order.car_info}</h3>
                                            <p className="text-sm text-mmd-silver/60">
                                                {order.client.prenom} {order.client.nom} - {order.client.telephone}
                                            </p>
                                            <p className="text-xs text-mmd-silver/40 mt-1">{formatDate(order.date)}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {getStatusBadge(order.statut, CAR_STATUSES)}
                                            <Select value={order.statut} onValueChange={(v) => updateCarStatus(order.id, v)}>
                                                <SelectTrigger className="w-36" data-testid={`car-order-status-${order.id}`}>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {CAR_STATUSES.map(s => (
                                                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
